/**
 * Componente de grade de horários
 */

import type { AvailabilityMeta } from "../types";
import { FiArrowRight, FiCalendar, FiCheck, FiSearch, FiUsers } from "react-icons/fi";

interface TimeSlotGridProps {
  slots: string[];
  /** Horários fora da grade regular, encaixados entre atendimentos já marcados. */
  fitSlots?: string[];
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  loadingSlots: boolean;
  providerSlots: Record<string, string[]>;
  selectedProviderId: string;
  availabilityMeta: AvailabilityMeta | null;
  requiresProviderSelection?: boolean;
  /** Próxima data (a partir da atual) com pelo menos um horário livre, se já encontrada. */
  suggestedDate?: string | null;
  /** Estamos sondando as próximas datas em busca de uma com horário livre. */
  loadingSuggestedDate?: boolean;
  onSelectSuggestedDate?: (date: string) => void;
  embedded?: boolean;
}

function formatSuggestedDateLabel(dateValue: string): string {
  const date = new Date(`${dateValue}T00:00:00`);
  const label = date.toLocaleDateString("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
  return label.charAt(0).toUpperCase() + label.slice(1).replace(".", "");
}

export function TimeSlotGrid({
  slots,
  fitSlots = [],
  selectedTime,
  onTimeSelect,
  loadingSlots,
  providerSlots,
  selectedProviderId,
  availabilityMeta,
  requiresProviderSelection = false,
  suggestedDate = null,
  loadingSuggestedDate = false,
  onSelectSuggestedDate,
  embedded = false,
}: TimeSlotGridProps) {
  const emptyReasonMessage =
    availabilityMeta?.reasonCode === "UNIT_CLOSED"
      ? availabilityMeta.details?.publicMessage ||
        availabilityMeta.reason ||
        "A unidade não terá expediente nesta data."
      : availabilityMeta?.reasonCode === "NO_PROVIDER_FOR_SELECTED_SERVICE" ||
          availabilityMeta?.reasonCode === "NO_PROVIDER_SUPPORTS_ALL_SERVICES"
        ? "Nenhum profissional ativo atende o serviço selecionado."
        : availabilityMeta?.reasonCode === "PROVIDERS_WITHOUT_AVAILABILITY_ON_DAY"
          ? "Nenhum profissional ativo atende nesta data."
          : "Tente outra data ou verifique a disponibilidade";

  const renderEmptyState = () => (
    <div className="animate-fade-in overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-5 py-8 text-center shadow-sm">
      <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-2xl text-primary-600 shadow-sm ring-1 ring-slate-100">
        <FiCalendar aria-hidden="true" />
      </div>
      <p className="font-bold text-slate-900">Nenhum horário livre nesta data</p>
      <p className="mx-auto mt-1 max-w-xs text-sm leading-6 text-slate-500">
        {emptyReasonMessage}
      </p>

      {loadingSuggestedDate ? (
        <div className="mx-auto mt-5 inline-flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-500">
          <FiSearch
            className="h-4 w-4 animate-pulse text-primary-500"
            aria-hidden="true"
          />
          Procurando a próxima data com horário livre...
        </div>
      ) : suggestedDate && onSelectSuggestedDate ? (
        <button
          type="button"
          onClick={() => onSelectSuggestedDate(suggestedDate)}
          className="animate-scale-in mx-auto mt-5 inline-flex min-h-[3rem] items-center gap-2.5 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 px-5 py-3 text-sm font-bold leading-none text-white shadow-md shadow-primary-600/20 transition hover:-translate-y-0.5 hover:from-primary-hover hover:to-primary-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
        >
          Ver horários de {formatSuggestedDateLabel(suggestedDate)}
          <FiArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}

      {availabilityMeta?.reasonCode && availabilityMeta.reasonCode !== "UNIT_CLOSED" && (
        <p className="mt-4 text-[11px] text-slate-400">
          Código interno: {availabilityMeta.reasonCode}
        </p>
      )}
    </div>
  );

  if (loadingSlots) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-center gap-3 text-sm font-semibold text-slate-600">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          <span>Carregando horários...</span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2.5 sm:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="h-11 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  if (requiresProviderSelection && !selectedProviderId) {
    return (
      <div className="rounded-2xl border border-dashed border-primary-200 bg-primary-50/60 px-4 py-8 text-center">
        <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-2xl text-primary-600 shadow-sm">
          <FiUsers aria-hidden="true" />
        </div>
        <p className="font-semibold text-slate-900">Escolha um profissional</p>
        <p className="mt-1 text-sm text-slate-600">
          Este estabelecimento exige a escolha de um barbeiro antes do horário.
        </p>
      </div>
    );
  }

  if (slots.length === 0) {
    return renderEmptyState();
  }

  const isAnyProvider = !selectedProviderId;
  const slotInfo: Array<{ time: string; capacity: number }> = [];

  if (isAnyProvider) {
    const capacityMap = new Map<string, number>();
    for (const t of slots) capacityMap.set(t, 0);
    for (const times of Object.values(providerSlots)) {
      for (const t of times) {
        if (capacityMap.has(t)) capacityMap.set(t, (capacityMap.get(t) || 0) + 1);
      }
    }
    for (const t of slots) {
      slotInfo.push({ time: t, capacity: capacityMap.get(t) || 0 });
    }
  } else {
    for (const t of slots) slotInfo.push({ time: t, capacity: 1 });
  }

  const visibleSlotInfo = isAnyProvider
    ? slotInfo.filter((item) => item.capacity > 0)
    : slotInfo;

  if (visibleSlotInfo.length === 0) {
    return renderEmptyState();
  }

  const fitSet = new Set(fitSlots);

  return (
    <section
      className={
        embedded
          ? ""
          : "rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm shadow-slate-200/70 sm:p-6"
      }
    >
      {!embedded && (
        <div className="mb-4">
          <h3 className="text-xl font-bold text-slate-900">Horários disponíveis</h3>
          <p className="text-sm text-slate-500">
            Escolha o melhor horário para continuar.
          </p>
        </div>
      )}

      <div
        className="grid grid-cols-3 gap-2.5 sm:grid-cols-4"
        role="listbox"
        aria-label="Horários disponíveis"
      >
        {visibleSlotInfo.map(({ time, capacity }) => {
          const selected = time === selectedTime;
          const isFit = fitSet.has(time);
          return (
            <button
              key={time}
              onClick={() => onTimeSelect(time)}
              className={`flex h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-xl border px-2 text-center text-lg font-semibold leading-none transition focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${
                selected
                  ? "border-primary-600 bg-gradient-to-r from-primary-600 to-primary-700 text-white"
                  : isFit
                    ? "border-accent-200 bg-accent-50/60 text-[#233055] hover:border-accent-500"
                    : "border-slate-200 bg-white text-[#233055] hover:border-primary-300"
              }`}
              role="option"
              aria-selected={selected}
              aria-label={isFit ? `${time}, encaixe entre atendimentos` : time}
              title={
                isFit
                  ? "Encaixe: começa assim que o atendimento anterior termina"
                  : undefined
              }
            >
              <span className="flex items-center justify-center gap-1.5">
                {selected && <FiCheck className="h-3.5 w-3.5" aria-hidden="true" />}
                <span>{time}</span>
                {capacity > 1 && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
                      selected
                        ? "bg-white/25 text-white"
                        : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    <FiUsers className="h-3 w-3" aria-hidden="true" />
                    {capacity}
                  </span>
                )}
              </span>
              {isFit && (
                <span
                  className={`text-[10px] font-bold uppercase leading-none tracking-wide ${
                    selected ? "text-white/80" : "text-accent-700"
                  }`}
                  aria-hidden="true"
                >
                  Encaixe
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
